// @flow
import * as React from 'react';
import { connect } from 'react-redux';
import {
  requestSearchResults,
  navigateSearchResults,
  clearSearch,
} from './searchActions';
import { CoreDataState, type CoreDatum } from 'ducks/coreDataReducer';
import type { AppState } from 'reducer';
import ContentRequest from 'pages/__components__/ContentRequest';
import HandleRequestError from 'containers/HandleRequestError';
import { type SearchState } from './searchReducer';
import {
  createNextPageQuery,
  DefaultPaginationPageSize,
  calculatePaginationDisplay,
} from './PaginationUtils';
import {
  NoItemsMessage,
  ClientErrorMessage,
  ServerErrorMessage,
} from './searchCollectionMessages';
import isEqual from 'lodash/isEqual';
import {
  Button,
  EmptyCard,
  CollectionItemTranisitionProvider,
} from 'components';
import type { SearchQuery } from './types';
import { bindActionCreators } from 'redux';
import { ugcSearch } from 'api/content/search';

type OwnProps = {
  label: string,
  clientId: Id,
  method: Function,
  CardComponent: () => React.Element<any>,
  locationSearch: string,
  onLocationSearchChange: string => void,
};

type PageProps = {
  number: number,
  query: SearchQuery,
};

type StateProps = {
  requestStatus: RequestStatus,
  items: Array<CoreDatum>,
  query: SearchQuery,
  pages: $PropertyType<SearchState, 'pages'>,
  currentPage: $PropertyType<SearchState, 'currentPage'>,
  totalPages: $PropertyType<SearchState, 'totalPages'>,
  prevPageProps?: ?PageProps,
  nextPageProps?: ?PageProps,
};
type DispatchProps = {
  dispatch: Function,
  requestSearchResults: typeof requestSearchResults,
  navigateSearchResults: typeof navigateSearchResults,
  clearSearch: typeof clearSearch,
};

type Props = OwnProps & StateProps & DispatchProps;

export class Collection extends React.PureComponent<Props, void> {
  componentDidMount() {
    this.props.requestSearchResults({
      query: this.props.query,
      method: this.props.method,
      page: this.props.currentPage,
      label: this.props.label,
    });
  }

  componentDidUpdate(prevProps: Props) {
    //
    if (!isEqual(prevProps.query, this.props.query)) {
      const pageIsLoaded = Array.isArray(
        this.props.pages.get(this.props.currentPage)
      );
      if (!pageIsLoaded) {
        this.props.requestSearchResults({
          query: this.props.query,
          method: this.props.method,
          page: this.props.currentPage,
          label: this.props.label,
        });
      } else {
        smoothscroll();
      }
    }

    if (prevProps.locationSearch !== this.props.locationSearch) {
      this.props.onLocationSearchChange(this.props.locationSearch);
    }
  }

  componentWillUnmount() {
    this.props.clearSearch();
  }

  handlePageNavigation = ({
    query,
    number,
  }: {
    query: { [string]: string },
    number: number,
  }) => {
    const pagingQuery = { ...query, page_number: number };
    this.props.navigateSearchResults({
      query: pagingQuery,
      method: this.props.method,
      page: number,
      label: this.props.label,
    });
  };

  render() {
    const {
      items,
      requestStatus = null,
      CardComponent,
      currentPage,
      pages,
      totalPages,
      nextPageProps,
      prevPageProps,
    } = this.props;

    return (
      <HandleRequestError
        requestStatus={requestStatus}
        match={err => typeof err.status === 'number' && err.status >= 500}
        render={ServerErrorMessage}
      >
        <HandleRequestError
          requestStatus={requestStatus}
          match={err => typeof err.status === 'number' && err.status >= 400}
          render={ClientErrorMessage}
        >
          <ContentRequest
            requestStatus={requestStatus}
            render={() => {
              if (items.length === 0 && currentPage === 1) {
                return NoItemsMessage();
              }

              if (items.length === 0) {
                return (
                  <EmptyCard>
                    <EmptyCard.Message>
                      <p>There are no more results</p>
                      <Button
                        onClick={() =>
                          prevPageProps != null &&
                          this.handlePageNavigation(prevPageProps)
                        }
                        className="SearchCollection_NavButton SearchCollection_NavButton--prev"
                      >
                        Go Back to Previous results.
                      </Button>
                    </EmptyCard.Message>
                  </EmptyCard>
                );
              }

              const hasMorePages =
                typeof totalPages === 'number' && totalPages > currentPage;
              const shouldHidePaginationNav =
                currentPage === 1 && items.length < 100 && !hasMorePages;

              return (
                <React.Fragment>
                  <Items
                    items={items}
                    pages={pages}
                    currentPage={currentPage}
                    totalPages={totalPages}
                  />
                  <CollectionItemTranisitionProvider
                    key={currentPage}
                    items={items}
                    itemKey={item => item.moderationState.getUGCId()}
                  >
                    {item => (
                      <CardComponent
                        datum={item}
                        dispatch={this.props.dispatch}
                        clientId={this.props.clientId}
                      />
                    )}
                  </CollectionItemTranisitionProvider>
                  {shouldHidePaginationNav ? null : (
                    <div className="flex">
                      <Button
                        onClick={() =>
                          prevPageProps &&
                          this.handlePageNavigation(prevPageProps)
                        }
                        className="SearchCollection_NavButton SearchCollection_NavButton--prev"
                        disabled={prevPageProps == null}
                      >
                        Previous
                      </Button>
                      <Button
                        style={{ marginLeft: 'auto' }}
                        className="SearchCollection_NavButton SearchCollection_NavButton--next"
                        onClick={() =>
                          nextPageProps &&
                          this.handlePageNavigation(nextPageProps)
                        }
                        disabled={nextPageProps == null}
                      >
                        Next
                      </Button>
                    </div>
                  )}
                </React.Fragment>
              );
            }}
          />
        </HandleRequestError>
      </HandleRequestError>
    );
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(Collection);

function Items({
  pages,
  currentPage,
  items,
  totalPages,
}: {
  pages: *,
  currentPage: number,
  items: Array<CoreDatum>,
  totalPages: ?number,
}) {
  const isFirstPage = currentPage === 1;
  const className = 'bg-silver white br-pill f6 pv1 ph2 PaginationStatus';

  if (isFirstPage && items.length === 1) {
    return null;
  }

  const hasMorePages =
    typeof totalPages === 'number' && totalPages > currentPage;

  if (
    isFirstPage &&
    items.length < DefaultPaginationPageSize &&
    !hasMorePages
  ) {
    return (
      <div style={{ margin: '-1em 0 -0.5em' }}>
        <span className={className} data-translate={true}>
          {items.length} items
        </span>
      </div>
    );
  }

  // Use shared pagination display logic
  const {
    first,
    last,
    total,
    potentiallyMoreItems,
  } = calculatePaginationDisplay({
    currentPage,
    currentPageCount: items.length,
    totalPages,
    getPageCount: pageNum => {
      const pageItems = coerceToArray(pages.get(pageNum));
      return pageItems.length;
    },
  });

  return (
    <div style={{ margin: '-1em 0 -0.5em' }}>
      <span className={className}>
        {first} - {last} of {total}
        {potentiallyMoreItems ? '+' : ''} items
      </span>
    </div>
  );
}

function getNavigation({
  queries,
  currentPage,
  pages,
  itemGetter,
  method,
  totalPages,
}: *): [?PageProps, ?PageProps] {
  let prevPage;
  let nextPage;
  // the first page serves as the base query
  const baseQuery = queries.get(1);
  const itemsInPage = coerceToArray(pages.get(currentPage));
  const prevPageNumber = currentPage - 1;
  const nextPageNumber = currentPage + 1;
  const itemsAreUnderPageSizeLimit =
    itemsInPage.length < DefaultPaginationPageSize;
  const hasMorePages =
    typeof totalPages === 'number' && totalPages > currentPage;

  // pagination isn't required if the first page is under page size and no more pages
  if (currentPage === 1 && itemsAreUnderPageSizeLimit && !hasMorePages) {
    return [prevPage, nextPage];
  }

  // corceToArray returns EmptyCollection, which seems to be bound to CoreDatum[] even
  // though its just an empty array
  // $FlowIssue
  const lastItemId: Id = itemsInPage[itemsInPage.length - 1];
  const lastItem = itemGetter(lastItemId);

  if (lastItem) {
    const nextPageValue = pages.get(nextPageNumber);
    const nextPageItemCount = Array.isArray(nextPageValue)
      ? nextPageValue.length
      : null;

    // nextPage
    if (hasMorePages) {
      nextPage =
        nextPageItemCount === 0
          ? nextPage
          : {
              number: nextPageNumber,
              query: baseQuery,
            };
    } else {
      nextPage =
        itemsAreUnderPageSizeLimit === false
          ? nextPageItemCount === 0
            ? nextPage
            : {
                number: nextPageNumber,
                // $FlowFixMe
                query:
                  method === ugcSearch
                    ? baseQuery
                    : createNextPageQuery(lastItem.ugc.created_date, baseQuery),
              }
          : nextPage;
    }
  }

  prevPage = queries.has(prevPageNumber)
    ? {
        number: prevPageNumber,
        query: queries.get(prevPageNumber),
      }
    : prevPage;

  // For ugcSearch with totalPages, always allow going back if not on page 1
  if (!prevPage && currentPage > 1 && hasMorePages) {
    prevPage = {
      number: prevPageNumber,
      query: baseQuery,
    };
  }

  return [prevPage, nextPage];
}

function smoothscroll() {
  const currentScroll = parseInt(
    (document.documentElement && document.documentElement.scrollTop) ||
      (document.body && document.body.scrollTop),
    10
  );
  if (currentScroll > 0) {
    window.requestAnimationFrame(smoothscroll);
    window.scrollTo(0, currentScroll - currentScroll / 5);
  }
}

function selectCoreData(
  coreData: CoreDataState,
  ids: Array<Id>
): Array<CoreDatum> {
  return ids.reduce((acc, id) => {
    const datum = CoreDataState.getTheThings(coreData, id);

    if (datum != null) {
      acc.push(datum);
    }

    return acc;
  }, []);
}

const EmptyCollectionArray = [];

function mapStateToProps(
  state: AppState & { search: SearchState },
  ownProps: OwnProps
): StateProps {
  const {
    search: { currentPage, pages, queries, totalPages },
    coreData,
  } = state;
  const query = queries.get(currentPage);
  const asyncItem = pages.get(currentPage);

  if (asyncItem instanceof Error) {
    return {
      requestStatus: asyncItem,
      items: EmptyCollectionArray,
      query,
      currentPage,
      pages,
      totalPages,
    };
  }

  if (Array.isArray(asyncItem)) {
    const items = selectCoreData(coreData, asyncItem);
    const [prevPageProps, nextPageProps] = getNavigation({
      queries,
      currentPage,
      pages,
      itemGetter: (id: Id) => CoreDataState.getTheThings(coreData, id),
      method: ownProps.method,
      totalPages,
    });

    return {
      requestStatus: 'complete',
      pages,
      items,
      query,
      currentPage,
      totalPages,
      prevPageProps,
      nextPageProps,
    };
  }

  return {
    requestStatus: 'pending',
    items: EmptyCollectionArray,
    query,
    currentPage,
    pages,
    totalPages,
  };
}

function mapDispatchToProps(dispatch: Function) {
  return {
    dispatch,
    ...bindActionCreators(
      {
        requestSearchResults,
        navigateSearchResults,
        clearSearch,
      },
      dispatch
    ),
  };
}

function coerceToArray(items: any): Array<*> {
  return Array.isArray(items) ? items : EmptyCollectionArray;
}



// WEBPACK FOOTER //
// ./src/containers/search/SearchCollection.js